// Where GoogleMap's own drawing goes, in the map's pixels (BL-018, package 4). DayMap's layout rules,
// ported from its 1000-unit drawing to screen pixels on a real map: pins one pin's room apart or closer
// sit in a ring round their middle; each label goes where it covers no other pin, dot or label (right,
// left, above, below, then further out); a label with no such place is left off, as Google's own
// labels are, and its pin keeps its name for a screen reader. A route runs through its stops in order.
//
// The projection is Google's (Web Mercator on a 256 px world at zoom 0). On Google's map it comes from
// the map itself (the overlay's projection); the stand-in fits the points as Map.fitBounds would
// (fitView), so both draw alike.
import type { GoogleMapData } from './map-schemas.ts';

/** A point's place in the map's box, in pixels from its top left. */
export type Project = (lat: number, lng: number) => [number, number];

const TILE = 256;
const wx = (lng: number) => ((lng + 180) / 360) * TILE;
const wy = (lat: number) => {
  const s = Math.sin((Math.max(-85, Math.min(85, lat)) * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE;
};
const unx = (x: number) => (x / TILE) * 360 - 180;
const uny = (y: number) => (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / TILE))) * 180) / Math.PI;

/** The ground one pixel covers at a latitude and zoom, in metres (Google's own figure). */
export const metresPerPixel = (lat: number, zoom: number) =>
  (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;

export interface FitOptions {
  /** Room kept clear round the points, in pixels. */
  pad?: number;
  /** The closest it comes in to fit them (a street or two). */
  maxZoom?: number;
  /** Where to look with no point at all. */
  center?: { lat: number; lng: number } | null;
}
/** The closest the map comes in when fitting its points, and where it looks with only one. */
export const FIT_MAX_ZOOM = 16;
export const FIT_PAD = 44;
/** With no point and no centre: the whole continent, so an empty map isn't a blank square. */
const NOWHERE = { lat: 50, lng: -100, zoom: 3 };

/** The view that fits the points, as Map.fitBounds does (zoom in quarter steps), and its projection. */
export function fitView(
  points: readonly { lat: number; lng: number }[],
  width: number,
  height: number,
  o: FitOptions = {},
): { zoom: number; center: { lat: number; lng: number }; project: Project } {
  const pad = o.pad ?? FIT_PAD;
  const maxZoom = o.maxZoom ?? FIT_MAX_ZOOM;
  let zoom: number;
  let cx: number;
  let cy: number;
  if (!points.length) {
    const c = o.center ?? NOWHERE;
    zoom = o.center ? maxZoom - 1 : NOWHERE.zoom;
    cx = wx(c.lng);
    cy = wy(c.lat);
  } else {
    const xs = points.map((p) => wx(p.lng));
    const ys = points.map((p) => wy(p.lat));
    cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const dx = Math.max(...xs) - Math.min(...xs);
    const dy = Math.max(...ys) - Math.min(...ys);
    const fits =
      dx > 0 || dy > 0
        ? Math.min(
            Math.log2(Math.max(1, width - 2 * pad) / (dx || 1e-9)),
            Math.log2(Math.max(1, height - 2 * pad) / (dy || 1e-9)),
          )
        : maxZoom;
    zoom = Math.max(3, Math.min(maxZoom, Math.floor(fits * 4) / 4));
  }
  const k = 2 ** zoom;
  return {
    zoom,
    center: { lat: uny(cy), lng: unx(cx) },
    project: (lat, lng) => [(wx(lng) - cx) * k + width / 2, (wy(lat) - cy) * k + height / 2],
  };
}

/** Every point the map should fit: the pins, the technicians and the shop. */
export const fitPoints = (d: GoogleMapData) => [
  ...d.pins.map((p) => ({ lat: p.lat, lng: p.lng })),
  ...d.techs.map((t) => ({ lat: t.lat, lng: t.lng })),
  ...(d.shop ? [{ lat: d.shop.lat, lng: d.shop.lng }] : []),
];

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}
/** Where a label's text starts, from its mark (the text's baseline at its left). */
export interface LabelAt {
  x: number;
  y: number;
}
export interface OverlayLayout {
  pins: { id: string; x: number; y: number; r: number; label: LabelAt | null }[];
  routes: { id: string; pen: number | null; points: [number, number][] }[];
  techs: { id: string; x: number; y: number; label: LabelAt | null; text: string }[];
  shop: { x: number; y: number; label: LabelAt | null } | null;
}

/** A pin's own room, in pixels: pins closer than this share a ring. */
export const PIN_ROOM = 22;
/** A visit's dot, and a visit under way's (drawn a little larger). */
const R = { pin: 8, now: 10, tech: 10, shop: 9 };
/** A label's line, and its width per letter at --fs-label (Archivo, 600). */
const LABEL_H = 16;
const PER_LETTER = 7.4;
/** The strip along the foot kept for Google's own marks (its logo and data line), or the stand-in's scale. */
const FOOT = 22;

const hit = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
const near = (x: number, y: number, w: number, h: number, m = 60) =>
  x > -m && y > -m && x < w + m && y < h + m;

/** A technician's dot's words: their name, and how long ago their iPad said so. */
export const techText = (t: GoogleMapData['techs'][number]) =>
  t.stale ? `${t.name}, last seen ${t.ago ?? 'a while ago'}` : t.ago ? `${t.name}, ${t.ago}` : t.name;

/** Where every mark and label goes on a map of this size, with this projection. */
export function overlayLayout(
  d: GoogleMapData,
  project: Project,
  width: number,
  height: number,
  selected?: string | null,
): OverlayLayout {
  // pins one pin's room apart or closer share a ring around their middle, in the day's order
  const raw = d.pins.map((p) => project(p.lat, p.lng));
  const groups: number[][] = [];
  raw.forEach(([x, y], i) => {
    const g = groups.find((m) => Math.hypot(raw[m[0]!]![0] - x, raw[m[0]!]![1] - y) < PIN_ROOM);
    if (g) g.push(i);
    else groups.push([i]);
  });
  const at: [number, number][] = [...raw];
  for (const g of groups) {
    if (g.length < 2) continue;
    const mx = g.reduce((t, i) => t + raw[i]![0], 0) / g.length;
    const my = g.reduce((t, i) => t + raw[i]![1], 0) / g.length;
    // far enough out that neighbours on the ring are a pin's room apart
    const r = Math.max(12, PIN_ROOM / (2 * Math.sin(Math.PI / g.length)));
    g.forEach((i, n) => {
      const t = (n / g.length) * Math.PI * 2 - Math.PI / 2;
      at[i] = [mx + r * Math.cos(t), my + r * Math.sin(t)];
    });
  }
  const techAt = d.techs.map((t) => project(t.lat, t.lng));
  const shopAt = d.shop ? project(d.shop.lat, d.shop.lng) : null;

  const own = (x: number, y: number, r: number): Box => ({
    x: x - r - 1,
    y: y - r - 1,
    w: 2 * r + 2,
    h: 2 * r + 2,
  });
  const taken: Box[] = [
    ...at.map(([x, y], i) => own(x, y, d.pins[i]!.state === 'now' ? R.now : R.pin)),
    ...techAt.map(([x, y]) => own(x, y, R.tech)),
    ...(shopAt ? [own(shopAt[0], shopAt[1], R.shop)] : []),
  ];
  const place = (x: number, y: number, text: string, r: number, must: boolean): LabelAt | null => {
    if (!near(x, y, width, height, 0)) return null;
    const w = text.length * PER_LETTER + 4;
    const h = LABEL_H;
    const tries = [
      [r + 6, -h / 2],
      [-r - 6 - w, -h / 2],
      [-w / 2, -r - 6 - h],
      [-w / 2, r + 6],
      [r + 6, -h / 2 - 18],
      [r + 6, -h / 2 + 18],
      [-r - 6 - w, -h / 2 - 18],
      [-r - 6 - w, -h / 2 + 18],
    ] as const;
    let best: Box | null = null;
    let fewest = Infinity;
    for (const [dx, dy] of tries) {
      const b = { x: x + dx, y: y + dy, w, h };
      if (b.x < 2 || b.y < 2 || b.x + w > width - 2 || b.y + h > height - FOOT) continue;
      const n = taken.filter((t) => hit(b, t)).length;
      if (n < fewest) {
        best = b;
        fewest = n;
      }
      if (!n) break;
    }
    if (!must && (!best || fewest > 0)) return null;
    best ??= { x: x + r + 6, y: y - h / 2, w, h };
    taken.push(best);
    return { x: best.x - x + 2, y: best.y - y + h - 4 };
  };

  // the chosen pin's name first, then the shop's, the technicians', and the rest in the day's order
  const order = d.pins
    .map((_, i) => i)
    .sort((a, b) => Number(d.pins[b]!.id === selected) - Number(d.pins[a]!.id === selected));
  const labels = new Map<number, LabelAt | null>();
  const chosen = order[0] !== undefined && d.pins[order[0]]!.id === selected ? order[0] : null;
  const pinLabel = (i: number) => {
    const p = d.pins[i]!;
    const [x, y] = at[i]!;
    labels.set(i, place(x, y, p.label, p.state === 'now' ? R.now : R.pin, i === chosen));
  };
  if (chosen !== null) pinLabel(chosen);
  const shop =
    d.shop && shopAt
      ? { x: shopAt[0], y: shopAt[1], label: place(shopAt[0], shopAt[1], d.shop.label, R.shop, false) }
      : null;
  const techs = d.techs.map((t, i) => {
    const [x, y] = techAt[i]!;
    const text = techText(t);
    return { id: t.id, x, y, text, label: place(x, y, text, R.tech, d.techs.length === 1) };
  });
  for (const i of order) if (i !== chosen) pinLabel(i);

  const pins = d.pins.map((p, i) => ({
    id: p.id,
    x: at[i]![0],
    y: at[i]![1],
    r: p.state === 'now' ? R.now : R.pin,
    label: labels.get(i) ?? null,
  }));
  const where = new Map(pins.map((p) => [p.id, [p.x, p.y] as [number, number]]));
  return {
    pins,
    routes: d.routes.map((r) => ({
      id: r.id,
      pen: r.pen,
      points: r.stops.map((id) => where.get(id)).filter((p): p is [number, number] => !!p),
    })),
    techs,
    shop,
  };
}

const SCALES_M = [50, 100, 200, 500, 1000, 2000, 5000, 10_000, 20_000, 50_000, 100_000, 200_000, 500_000];

/** The stand-in's scale: a round distance about a fifth of the box across, its length and its words. */
export function scaleBar(lat: number, zoom: number, width: number): { length: number; words: string } {
  const mpp = metresPerPixel(lat, zoom);
  const want = (width / 5) * mpp;
  const m = [...SCALES_M].reverse().find((u) => u <= want) ?? SCALES_M[0]!;
  return { length: m / mpp, words: m < 1000 ? `${m} m` : `${m / 1000} km` };
}
